import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PagesPage from './pages/PagesPage';
import PageFormPage from './pages/PageFormPage';
import JobsPage from './pages/JobsPage';
import JobFormPage from './pages/JobFormPage';
import ApplicationsPage from './pages/ApplicationsPage';
import ApplicationDetailsPage from './pages/ApplicationDetailsPage';
import UsersPage from './pages/UsersPage';
import UserFormPage from './pages/UserFormPage';
import SettingsPage from './pages/SettingsPage';
// Public pages
import PublicHomePage from './pages/PublicHomePage';
import PublicJobsPage from './pages/PublicJobsPage';
import JobApplicationPage from './pages/JobApplicationPage';
import ApplicationSuccessPage from './pages/ApplicationSuccessPage';
import PublicPageView from './pages/PublicPageView';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import GoogleAnalytics from './components/GoogleAnalytics';
import MetaDescription from './components/MetaDescription';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, setIntendedDestination } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search;
    setIntendedDestination(currentPath);
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, loading, setIntendedDestination } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search;
    setIntendedDestination(currentPath);
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin' && !user?.permissions?.includes('manage_users')) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Settings Route Component
const SettingsRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, loading, setIntendedDestination } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search;
    setIntendedDestination(currentPath);
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin' && !user?.permissions?.includes('manage_settings')) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Access Denied
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              You don't have permission to access the settings page. 
              Please contact an administrator if you need access to manage website settings.
            </p>
            <div className="flex justify-center space-x-4">
              <Link
                to="/backoffice/dashboard"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Return to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Pages Route Component
const PagesRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, loading, setIntendedDestination } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search;
    setIntendedDestination(currentPath);
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin' && !user?.permissions?.includes('edit_pages')) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Access Denied
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              You don't have permission to access the pages management. 
              Please contact an administrator if you need access to edit website pages.
            </p>
            <div className="flex justify-center space-x-4">
              <Link
                to="/backoffice/dashboard"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Return to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Jobs Route Component
const JobsRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, loading, setIntendedDestination } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search;
    setIntendedDestination(currentPath);
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin' && !user?.permissions?.includes('manage_jobs')) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Access Denied
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              You don't have permission to access the jobs management. 
              Please contact an administrator if you need access to manage job openings.
            </p>
            <div className="flex justify-center space-x-4">
              <Link
                to="/backoffice/dashboard"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Return to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Applications Route Component
const ApplicationsRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, loading, setIntendedDestination } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search;
    setIntendedDestination(currentPath);
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin' && !user?.permissions?.includes('review_applications')) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Access Denied
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              You don't have permission to access the applications management. 
              Please contact an administrator if you need access to review job applications.
            </p>
            <div className="flex justify-center space-x-4">
              <Link
                to="/backoffice/dashboard"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Return to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicHomePage />} />
      <Route path="/jobs" element={<PublicJobsPage />} />
      <Route path="/jobs/:id/apply" element={<JobApplicationPage />} />
      <Route path="/application/success" element={<ApplicationSuccessPage />} />
      <Route path="/page/:slug" element={<PublicPageView />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/backoffice" replace /> : <LoginPage />
      } />
      
      {/* Backoffice Routes */}
      <Route path="/backoffice" element={
        <ProtectedRoute>
          <Layout>
            <DashboardPage />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/backoffice/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <DashboardPage />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/backoffice/pages" element={
        <PagesRoute>
          <Layout>
            <PagesPage />
          </Layout>
        </PagesRoute>
      } />
      
      <Route path="/backoffice/pages/new" element={
        <PagesRoute>
          <Layout>
            <PageFormPage />
          </Layout>
        </PagesRoute>
      } />
      
      <Route path="/backoffice/pages/:id/edit" element={
        <PagesRoute>
          <Layout>
            <PageFormPage />
          </Layout>
        </PagesRoute>
      } />
      
      <Route path="/backoffice/jobs" element={
        <JobsRoute>
          <Layout>
            <JobsPage />
          </Layout>
        </JobsRoute>
      } />
      
      <Route path="/backoffice/jobs/new" element={
        <JobsRoute>
          <Layout>
            <JobFormPage />
          </Layout>
        </JobsRoute>
      } />
      
      <Route path="/backoffice/jobs/:id/edit" element={
        <JobsRoute>
          <Layout>
            <JobFormPage />
          </Layout>
        </JobsRoute>
      } />
      
      <Route path="/backoffice/applications" element={
        <ApplicationsRoute>
          <Layout>
            <ApplicationsPage />
          </Layout>
        </ApplicationsRoute>
      } />
      
      <Route path="/backoffice/applications/:id" element={
        <ApplicationsRoute>
          <Layout>
            <ApplicationDetailsPage />
          </Layout>
        </ApplicationsRoute>
      } />
      
      <Route path="/backoffice/users" element={
        <AdminRoute>
          <Layout>
            <UsersPage />
          </Layout>
        </AdminRoute>
      } />
      
      <Route path="/backoffice/users/new" element={
        <AdminRoute>
          <Layout>
            <UserFormPage />
          </Layout>
        </AdminRoute>
      } />
      
      <Route path="/backoffice/users/:id/edit" element={
        <AdminRoute>
          <Layout>
            <UserFormPage />
          </Layout>
        </AdminRoute>
      } />
      
      <Route path="/backoffice/settings" element={
        <SettingsRoute>
          <Layout>
            <SettingsPage />
          </Layout>
        </SettingsRoute>
      } />
      
      {/* Catch all route - only for truly unknown routes */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <GoogleAnalytics />
        <MetaDescription />
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
};

export default App;
